import React, { useEffect, useRef } from 'react';
import { COLORS } from '../utils/constants';
import useVisualViewport from '../hooks/useVisualViewport';

/**
 * Popup flotante unificado para toda la app.
 *
 * Posicionado desde arriba (top: 60px), con max-height que se adapta al
 * viewport visual. Al abrirse el teclado el popup se encoge por abajo
 * (sin saltar) y hace scroll interno para mostrar el input enfocado.
 *
 * Props:
 *   onClose   — función al pulsar backdrop o botón ✕
 *   title     — si se pasa, muestra cabecera con título y botón ✕
 *   zIndex    — por defecto 500
 *   flex      — si true, el contenedor es flex-column (para header fijo + scroll interno)
 *
 * El prop `type` ya no tiene efecto (eliminado); todos los popups son flotantes.
 *
 * EXCEPCIÓN: AddFoodModal y RecipesScreen usar/editar usan MaximizedPopup
 * porque necesitan la pantalla completa para búsqueda + sugerencias.
 */
export default function Popup({ onClose, title, children, zIndex = 500, flex = false, scrollId }) {
  const { height: vpHeight, offsetTop } = useVisualViewport();
  const containerRef = useRef(null);

  useEffect(() => {
    const el = document.activeElement;
    const container = containerRef.current;
    if (!el || !container || !container.contains(el)) return;
    setTimeout(() => {
      const elRect = el.getBoundingClientRect();
      const contRect = container.getBoundingClientRect();
      const pad = 16;
      if (elRect.bottom > contRect.bottom - pad) {
        container.scrollTop += (elRect.bottom - contRect.bottom) + pad;
      }
    }, 150);
  }, [vpHeight, offsetTop]);

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: '#000b', zIndex: zIndex - 1 }} />
      <div
        ref={containerRef}
        id={scrollId}
        onClick={e => e.stopPropagation()}
        style={{
          position: 'fixed',
          top: offsetTop + 60,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 'calc(100% - 32px)',
          maxWidth: 420,
          maxHeight: vpHeight - 80,
          background: 'rgba(28, 27, 27, 0.97)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          border: '1px solid rgba(255,255,255,0.07)',
          borderRadius: 20,
          zIndex,
          overflowY: flex ? 'hidden' : 'auto',
          overscrollBehavior: 'contain',
          overflowX: 'hidden',
          padding: '24px 20px',
          boxSizing: 'border-box',
          boxShadow: '0 20px 60px rgba(0,0,0,0.85), 0 0 0 1px rgba(255,255,255,0.04)',
          ...(flex ? { display: 'flex', flexDirection: 'column' } : {}),
        }}
      >
        {title != null && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexShrink: 0 }}>
            <span style={{ color: COLORS.text, fontSize: 18, fontWeight: 800 }}>{title}</span>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: COLORS.textMuted, fontSize: 24, cursor: 'pointer', lineHeight: 1, padding: 0 }}>✕</button>
          </div>
        )}
        {children}
      </div>
    </>
  );
}
